import { useNavigate } from 'react-router-dom'
import { useUnviewedActionPlans, useMarkViewed } from '@/hooks/use-notifications'
import type { UnviewedActionPlan } from '@/types'

// Format relative time (e.g., "2 days ago")
function timeAgo(timestamp: number): string {
  const seconds = Math.floor(Date.now() / 1000 - timestamp)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return `${Math.floor(days / 30)}mo ago`
}

// Status badge colors matching existing action-plan-status-badge pattern
const statusColors: Record<string, string> = {
  active: 'bg-blue-100 text-blue-700',
  expired: 'bg-red-100 text-red-700',
}

export function Notifications() {
  const navigate = useNavigate()
  const { data: plans, isLoading } = useUnviewedActionPlans()
  const markViewed = useMarkViewed()

  const handlePlanClick = (plan: UnviewedActionPlan) => {
    markViewed.mutate([plan.id])
    navigate(`/metrics/${plan.metric_id}`)
  }

  const handleMarkAll = () => {
    if (!plans || plans.length === 0) return
    markViewed.mutate(plans.map(p => p.id))
  }

  return (
    <div>
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Action Plans to Review</h1>
          <p className="text-sm text-gray-500 mt-1">
            New action plans from your team that you haven't viewed yet
          </p>
        </div>
        {plans && plans.length > 0 && (
          <button
            onClick={handleMarkAll}
            disabled={markViewed.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Mark All as Viewed
          </button>
        )}
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-2/3 mb-3" />
                  <div className="flex gap-3">
                    <div className="h-5 bg-gray-100 rounded-full w-20" />
                    <div className="h-5 bg-gray-100 rounded w-24" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && (!plans || plans.length === 0) && (
        <div className="text-center py-16">
          <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-sm font-medium text-gray-900 mb-1">All caught up</h3>
          <p className="text-sm text-gray-500">No new action plans to review</p>
        </div>
      )}

      {/* Action plan cards */}
      {plans && plans.length > 0 && (
        <div className="space-y-3">
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => handlePlanClick(plan)}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-medium text-gray-900 truncate">{plan.title}</h3>
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[plan.status] || 'bg-gray-100 text-gray-700'}`}>
                      {plan.status}
                    </span>
                  </div>
                  {plan.description && (
                    <p className="text-sm text-gray-500 line-clamp-2 mb-2">{plan.description}</p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-gray-400">
                    {/* Metric badge */}
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">
                      {plan.metric_name}
                    </span>
                    {/* Owner */}
                    <span className="flex items-center gap-1">
                      {plan.owner_avatar ? (
                        <img src={plan.owner_avatar} alt="" className="w-4 h-4 rounded-full" />
                      ) : (
                        <div className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-[10px] text-gray-500">
                          {plan.owner_name?.charAt(0)}
                        </div>
                      )}
                      {plan.owner_name}
                    </span>
                    {/* Time ago */}
                    <span>{timeAgo(plan.created_at)}</span>
                  </div>
                </div>
                {/* Chevron */}
                <svg className="w-5 h-5 text-gray-300 flex-shrink-0 ml-3 mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
